'use client';
import Icon from '@/components/common/Icon';
import { menuItems } from '@/components/layout/menuConfig'; // Import account menu items
import { useStateContext } from '@/context/StateContext';
import styles from '@/styles/Header.module.scss';
import { Avatar, Button, Dropdown, Tooltip } from 'antd';
import { signIn, signOut } from 'next-auth/react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

const NavBar = dynamic(() => import('./NavBar'), { ssr: false });
const DynamicCart = dynamic(() => import('@/components/Cart'), { ssr: false });

const Header = ({ session, initialIsWhiteBackground }) => {
  const pathname = usePathname();
  const [isMounted, setIsMounted] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const { showCart, setShowCart, totalQuantities } = useStateContext();

  useEffect(() => {
    setIsMounted(true);
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
      if (window.innerWidth >= 992) {
        setIsMobileNavOpen(false);
      }
    };
    handleResize(); // Check on initial render
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const linkClass = initialIsWhiteBackground
    ? 'header-nav-link on-white w-nav-link'
    : 'header-nav-link white w-nav-link';

  const navClass = initialIsWhiteBackground
    ? 'position-absolute full header-bg'
    : 'position-absolute full header-bg dark';

  const logoClass = initialIsWhiteBackground ? '-light' : '';

  const loginClass = initialIsWhiteBackground
    ? 'header-nav-link login hidden-on-tablet w-inline-block'
    : 'header-nav-link login hidden-on-tablet white w-inline-block';

  const [opacity, setOpacity] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const newOpacity = Math.min(window.scrollY / 20, 1);
      setOpacity(newOpacity);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const [isDropdownOpen, setIsDropdownOpen] = useState({
    Products: false,
    Resources: false,
  });

  // Close all dropdowns when pathname changes (page navigation)
  useEffect(() => {
    const closeAllDropdowns = () => {
      setIsDropdownOpen({
        Products: false,
        Resources: false,
      });
    };

    closeAllDropdowns();
  }, [pathname]);

  const handleMouseEnter = useCallback((name) => {
    setIsDropdownOpen((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleMouseLeave = useCallback((name) => {
    setIsDropdownOpen((prev) => ({ ...prev, [name]: false }));
  }, []);

  const handleMobileNavToggle = useCallback(() => {
    setIsMobileNavOpen((prev) => !prev);
  }, []);

  // Define base navItems
  const navItems = [
    {
      name: 'Products',
      isDropdown: true,
      dropdownContent: [
        {
          title: 'Products',
          links: [
            {
              name: 'Mark Certificates',
              path: '/products/digicert-mark-certificates',
            },
            {
              name: 'Verified Mark Certificate',
              path: '/products/digicert-verified-mark-certificate',
            },
            {
              name: 'Common Mark Certificate',
              path: '/products/digicert-common-mark-certificate',
            },
            {
              name: 'Government Mark Certificate',
              path: '/products/digicert-government-mark-certificate',
            },
            {
              name: 'DMARC Enforcement',
              path: '/products/easydmarc-enforcement',
            },
          ],
        },
      ],
    },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'About', path: '/about' },
    {
      name: 'Resources',
      isDropdown: true,
      dropdownContent: [
        {
          title: 'Support',
          links: [
            { name: 'Help Center', path: '/help-center' },
            { name: 'Articles', path: '/articles' },
            { name: 'FAQs', path: '/faq' },
            { name: 'Glossary', path: '/glossary' },
          ],
        },
        {
          title: 'Tools',
          links: [
            { name: 'VMC Record Checker', path: '/vmc-validator' },
            { name: 'BIMI Record Checker', path: '/bimi-checker' },
            { name: 'DMARC Record Checker', path: '/dmarc-inspector' },
          ],
        },
      ],
    },
    { name: 'Help Center', path: '/help-center' },
  ];

  // Organize menu items for logged-in users
  const getNavItems = useMemo(() => {
    if (!session) return navItems;

    // Add account menu items to Resources dropdown
    return navItems.map((item) => {
      if (item.name === 'Resources') {
        return {
          ...item,
          dropdownContent: [
            ...item.dropdownContent,
            {
              title: 'Account Tools',
              links: menuItems
                .filter((menuItem) => !menuItem.children)
                .map((menuItem) => ({
                  name: menuItem.label,
                  path: `/account/${menuItem.key}`,
                })),
            },
            {
              title: 'Email Tools',
              links:
                menuItems
                  .find((item) => item.key === 'tools')
                  ?.children.map((tool) => ({
                    name: tool.label,
                    path: `/account/${tool.key}`,
                  })) || [],
            },
          ],
        };
      }
      return item;
    });
  }, [session]);

  // Update memoizedNavBar to use getNavItems
  const memoizedNavBar = useMemo(
    () => (
      <NavBar
        linkClass={linkClass}
        isDropdownOpen={isDropdownOpen}
        setIsDropdownOpen={setIsDropdownOpen}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        isMobileNavOpen={isMobileNavOpen}
        handleMobileNavToggle={handleMobileNavToggle}
        isDesktop={isDesktop}
        navItems={getNavItems}
        session={session}
      />
    ),
    [
      linkClass,
      isDropdownOpen,
      isMobileNavOpen,
      isDesktop,
      getNavItems,
      session,
      handleMouseEnter,
      handleMouseLeave,
      handleMobileNavToggle,
    ]
  );

  const handleCartClick = (e) => {
    e.preventDefault(); // Prevent default link behavior
    setShowCart((prev) => !prev); // Toggle cart visibility
  };

  // Helper function to get user initials
  const getUserInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // User menu items
  const userMenuItems = {
    items: [
      {
        key: 'dashboard',
        label: <Link href="/account/dashboard">Dashboard</Link>,
        icon: <Icon name="DashboardOutlined" className={styles.userMenuIcon} />,
      },
      {
        key: 'profile',
        label: <Link href="/account/settings/profile">Profile</Link>,
        icon: <Icon name="UserOutlined" className={styles.userMenuIcon} />,
      },
      {
        key: 'orders',
        label: <Link href="/account/orders">Orders</Link>,
        icon: <Icon name="ShoppingOutlined" className={styles.userMenuIcon} />,
      },
      {
        key: 'billing',
        label: <Link href="/account/billing/transactions">Billing</Link>,
        icon: <Icon name="CreditCardOutlined" className={styles.userMenuIcon} />,
      },
      {
        key: 'manage-data',
        label: <Link href="/account/settings/organizations">Manage Data</Link>,
        icon: <Icon name="DatabaseOutlined" className={styles.userMenuIcon} />,
      },
      {
        key: 'help',
        label: <Link href="/account/support">Support</Link>,
        icon: <Icon name="MessageOutlined" className={styles.userMenuIcon} />,
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        label: 'Sign Out',
        icon: <Icon name="LogoutOutlined" className={styles.userMenuIcon} />,
        onClick: () => signOut({ callbackUrl: '/auth/signin' }),
      },
    ],
  };

  if (!isMounted) {
    return null; // Return null instead of HeaderSkeleton
  }

  return (
    <>
      {session && !pathname.startsWith('/account') && (
        <Link href="/account/dashboard">
          <Button
            type="primary"
            icon={<Icon name="DashboardOutlined" />}
            className="floating-dashboard-btn"
            size={!isDesktop ? 'middle' : 'large'}
          >
            {isDesktop ? 'Dashboard' : ''}
          </Button>
        </Link>
      )}
      <div role="banner" className="header-wrapper w-nav">
        <div className="position-relative z-index-1">
          <div className="container-wide w-container">
            <div
              className={`header-content-wrapper ${pathname.startsWith('/account') ? 'is-account-page' : ''}`}
            >
              <Link href="/" className="header-logo-link w-nav-brand">
                <Image
                  src={`/images/logo${logoClass}.svg`}
                  alt="Brandymail.com logo"
                  className="header-logo"
                  width={150}
                  height={50}
                />
              </Link>
              {!pathname.startsWith('/account') && (
                <div className="header-middle">{memoizedNavBar}</div>
              )}
              <div className="header-right-side">
                <Tooltip placement="bottom" title="View your shopping cart">
                  <Link href="" className={loginClass} onClick={handleCartClick}>
                    <div style={{ position: 'relative' }}>
                      <Icon
                        name="ShoppingOutlined"
                        className="color-accent-1"
                        size={28}
                        style={{ marginRight: '12px' }}
                      />
                      {totalQuantities > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '8px',
                            height: '8px',
                            width: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#05c168',
                          }}
                        ></div>
                      )}
                    </div>
                  </Link>
                </Tooltip>
                {showCart && <DynamicCart />}
                {!session && (
                  <div>
                    <Tooltip placement="bottom" title="Sign in to your account">
                      <Link href="#" className={loginClass} onClick={() => signIn()}>
                        <Icon name="LoginOutlined" className="color-accent-1" size={28} />
                      </Link>
                    </Tooltip>
                  </div>
                )}
                {session && (
                  <div>
                    <Dropdown menu={userMenuItems} placement="bottomRight" trigger={['click']}>
                      <div
                        className="user-menu-trigger"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          transition: 'background-color 0.3s',
                          flexShrink: 0, // Prevent entire menu from shrinking
                        }}
                      >
                        <Avatar
                          style={{
                            backgroundColor: '#05c168',
                            marginRight: '8px',
                            flexShrink: 0, // Prevent shrinking in flex container
                          }}
                        >
                          {getUserInitials(session.user.name)}
                        </Avatar>
                        {/* <span
                          className="user-name"
                          style={{
                            marginRight: '8px',
                            display: isDesktop ? 'block' : 'none',
                          }}
                        >
                          {session.user.name}
                        </span> */}
                      </div>
                    </Dropdown>
                  </div>
                )}
                <button
                  className={`hamburger-menu-wrapper w-nav-button ${
                    isMobileNavOpen ? 'w--open' : ''
                  }`}
                  onClick={handleMobileNavToggle}
                  style={{
                    display: isDesktop ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '44px',
                    height: '44px',
                    minWidth: '44px',
                    minHeight: '44px',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  aria-label={isMobileNavOpen ? 'Close menu' : 'Open menu'}
                >
                  <Icon
                    name={isMobileNavOpen ? 'X' : 'Menu2'}
                    size={32}
                    stroke={2}
                    style={{ color: '#fff' }}
                  />
                </button>
                <a
                  href="/products/digicert-verified-mark-certificate"
                  className="btn-primary small header-btn-hidde-on-tablet w-button"
                  rel="noreferrer"
                  style={{
                    padding: '10px 20px',
                    fontSize: '14px',
                  }}
                >
                  Buy Now
                </a>
              </div>
            </div>
          </div>
          <div
            className={navClass}
            style={{
              opacity: initialIsWhiteBackground ? 1 : opacity,
            }}
          ></div>
        </div>
        {/* Mobile Nav Overlay */}
        {isMounted && (
          <div
            className={`w-nav-overlay ${isMobileNavOpen ? 'w--nav-overlay-open' : ''}`}
            style={{
              display: isMobileNavOpen ? 'block' : 'none',
              height: isMobileNavOpen ? '100vh' : '0',
            }}
          >
            {memoizedNavBar}
          </div>
        )}
      </div>
    </>
  );
};

export default Header;
